<div class="global-catalog-container">
  <div class="page-header">
    <h2>Global Product Catalog</h2>
    <p class="subtitle">Manage platform-wide and chain-level product templates</p>
  </div>

  <nb-tabset (changeTab)="onCatalogTabChange($event)">
    <!-- ======================== PLATFORM CATALOG ======================== -->
    <nb-tab tabTitle="Platform Catalog" tabIcon="globe-outline">
      <div class="tab-content">
        <!-- Toolbar -->
        <div class="toolbar">
          <div class="search-filters">
            <div class="input-group search-field">
              <input type="text" class="form-control" placeholder="Search by name or barcode"
                [(ngModel)]="platformSearch" (keyup.enter)="onPlatformSearch()">
              <button class="btn btn-outline-secondary" type="button" (click)="onPlatformSearch()">
                <nb-icon icon="search-outline"></nb-icon>
              </button>
            </div>

            <div class="filter-chips">
              <label class="filter-label">Filters:</label>
              <input type="text" class="form-control form-control-sm filter-text-input"
                placeholder="Main Group" [(ngModel)]="platformMainGroup" (keyup.enter)="onPlatformSearch()">
              <input type="text" class="form-control form-control-sm filter-text-input"
                placeholder="Discount Group" [(ngModel)]="platformDiscountGroup" (keyup.enter)="onPlatformSearch()">
              <select class="form-select form-select-sm filter-text-input" style="min-width: 200px;"
                [(ngModel)]="platformGlobalCategoryId" (ngModelChange)="onPlatformSearch()">
                <option value="">All categories</option>
                <option value="none">Uncategorised</option>
                <option *ngFor="let cat of globalCategoriesForFilter" [value]="cat._id">{{ categoryFilterLabel(cat) }}</option>
              </select>
              <button class="btn btn-sm"
                [ngClass]="platformAgeRestricted === true ? 'btn-warning' : 'btn-outline-secondary'"
                (click)="platformAgeRestricted = platformAgeRestricted === true ? null : true; onPlatformSearch()">
                <nb-icon icon="alert-triangle-outline"></nb-icon> Age Restricted
              </button>
              <button class="btn btn-sm"
                [ngClass]="platformHasDeposit === true ? 'btn-primary' : 'btn-outline-secondary'"
                (click)="platformHasDeposit = platformHasDeposit === true ? null : true; onPlatformSearch()">
                <nb-icon icon="swap-outline"></nb-icon> Has Deposit
              </button>
              <button class="btn btn-sm btn-link text-muted" (click)="clearPlatformFilters()"
                *ngIf="platformSearch || platformAgeRestricted !== null || platformHasDeposit !== null || platformMainGroup || platformDiscountGroup || platformGlobalCategoryId">
                <nb-icon icon="close-outline"></nb-icon> Clear
              </button>
            </div>
          </div>

          <div class="d-flex gap-2 align-items-center">
            <button class="btn btn-outline-success" (click)="openImportDialog()">
              <nb-icon icon="upload-outline"></nb-icon> Import CSV
            </button>
            <div class="reference-menu-wrap">
              <button class="btn btn-outline-secondary" type="button" (click)="showReferenceMenu = !showReferenceMenu"
                [attr.aria-expanded]="showReferenceMenu">
                <nb-icon icon="more-horizontal-outline"></nb-icon> More
              </button>
              <div class="reference-menu-dropdown" *ngIf="showReferenceMenu">
                <button type="button" class="dropdown-item" (click)="openReferenceImportDialog('dsk')">
                  Import DSK groups CSV
                </button>
                <button type="button" class="dropdown-item" (click)="openReferenceImportDialog('vendor')">
                  Import vendors CSV
                </button>
              </div>
            </div>
            <button class="btn btn-primary" (click)="openAddDialog()">
              <nb-icon icon="plus-outline"></nb-icon> Add Global Product
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div class="loading-container" *ngIf="loading">
          <ngx-spinner name="globalCatalog" bdColor="transparent" size="medium" color="#3366ff" type="ball-spin-fade"></ngx-spinner>
          <p>Loading products…</p>
        </div>

        <!-- Empty state -->
        <div class="empty-state" *ngIf="!loading && platformProducts.length === 0">
          <nb-icon icon="shopping-bag-outline" class="empty-icon"></nb-icon>
          <h3>No global products found</h3>
          <p>Create your first global product template to sync across stores.</p>
          <button class="btn btn-primary" (click)="openAddDialog()">Add Global Product</button>
        </div>

        <!-- Products table -->
        <div class="table-container" *ngIf="!loading && platformProducts.length > 0">
          <table class="products-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Barcode</th>
                <th>SKU</th>
                <th>Age Restricted</th>
                <th>Has Deposit</th>
                <th>Linked Stores</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let product of platformProducts">
                <td>
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-tags" *ngIf="product.tags?.length">
                    <span class="tag" *ngFor="let tag of product.tags">{{ tag }}</span>
                  </div>
                </td>
                <td class="text-muted small">{{ product.globalCategory?.path || '—' }}</td>
                <td>{{ product.barcode || '—' }}</td>
                <td>{{ product.sku || '—' }}</td>
                <td>
                  <span class="status-chip" [ngClass]="product.ageRestricted ? 'chip-warn' : 'chip-neutral'">
                    {{ product.ageRestricted ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="status-chip" [ngClass]="product.hasDeposit ? 'chip-info' : 'chip-neutral'">
                    {{ product.hasDeposit ? product.depositCategory || 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <button class="btn btn-outline-secondary btn-sm" (click)="openLinkedProductsDialog(product)"
                    [title]="'View linked store products'">
                    <nb-icon icon="link-2-outline"></nb-icon>
                    {{ product.linkedProductsCount ?? (product.linkedProducts?.length ?? 0) }}
                  </button>
                </td>
                <td class="actions-cell">
                  <button class="btn btn-sm btn-outline-primary" (click)="openEditDialog(product)" title="Edit">
                    <nb-icon icon="edit-2-outline"></nb-icon>
                  </button>
                  <button class="btn btn-sm btn-outline-warning" (click)="syncProduct(product)" title="Force sync to linked stores">
                    <nb-icon icon="sync-outline"></nb-icon>
                  </button>
                  <button class="btn btn-sm btn-outline-danger" (click)="deleteProduct(product)" title="Delete">
                    <nb-icon icon="trash-2-outline"></nb-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Platform Pagination -->
        <nb-card-footer *ngIf="platformTotalPages > 1">
          <div class="d-flex justify-content-between align-items-center">
            <div class="pagination-info">
              Showing {{ (platformPage - 1) * platformLimit + 1 }} to
              {{ Math.min(platformPage * platformLimit, platformTotal) }} of
              {{ platformTotal }} products
            </div>
            <nav aria-label="Global catalog pagination">
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" [class.disabled]="platformPage <= 1">
                  <button class="page-link" (click)="onPlatformPageChange(platformPage - 1)" [disabled]="platformPage <= 1">
                    Previous
                  </button>
                </li>
                <li class="page-item" [class.active]="platformPage === 1" *ngIf="platformTotalPages > 1">
                  <button class="page-link" (click)="onPlatformPageChange(1)">1</button>
                </li>
                <li class="page-item disabled" *ngIf="platformPage > 4">
                  <span class="page-link">...</span>
                </li>
                <li class="page-item"
                  [class.active]="platformPage === p"
                  *ngFor="let p of getPlatformPaginationPages()"
                  [attr.aria-current]="platformPage === p ? 'page' : null">
                  <button class="page-link" (click)="onPlatformPageChange(p)">{{ p }}</button>
                </li>
                <li class="page-item disabled" *ngIf="platformPage < platformTotalPages - 3">
                  <span class="page-link">...</span>
                </li>
                <li class="page-item" [class.active]="platformPage === platformTotalPages" *ngIf="platformTotalPages > 1 && platformPage !== platformTotalPages">
                  <button class="page-link" (click)="onPlatformPageChange(platformTotalPages)">{{ platformTotalPages }}</button>
                </li>
                <li class="page-item" [class.disabled]="platformPage >= platformTotalPages">
                  <button class="page-link" (click)="onPlatformPageChange(platformPage + 1)" [disabled]="platformPage >= platformTotalPages">
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </nb-card-footer>
      </div>
    </nb-tab>

    <nb-tab tabTitle="Categories" tabIcon="folder-outline">
      <div class="tab-content">
        <app-global-categories-tab [isAdmin]="isAdmin" (categoriesChanged)="loadGlobalCategoriesForFilter()"></app-global-categories-tab>
      </div>
    </nb-tab>

    <!-- ======================== CHAIN CATALOG ======================== -->
    <nb-tab tabTitle="Chain Catalog" tabIcon="link-2-outline" *ngIf="showChainTab">
      <div class="tab-content">
        <div class="toolbar">
          <p class="chain-info" *ngIf="chainId">Chain ID: {{ chainId }}</p>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-success" (click)="openChainImportDialog()">
              <nb-icon icon="upload-outline"></nb-icon> Import CSV
            </button>
            <button class="btn btn-primary" (click)="openAddChainDialog()">
              <nb-icon icon="plus-outline"></nb-icon> Add Chain Product
            </button>
          </div>
        </div>

        <div class="loading-container" *ngIf="chainLoading">
          <p>Loading chain products…</p>
        </div>

        <div class="empty-state" *ngIf="!chainLoading && chainProducts.length === 0">
          <nb-icon icon="shopping-bag-outline" class="empty-icon"></nb-icon>
          <h3>No chain products found</h3>
          <p>Add chain-level product templates for this chain.</p>
        </div>

        <div class="table-container" *ngIf="!chainLoading && chainProducts.length > 0">
          <table class="products-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Barcode</th>
                <th>SKU</th>
                <th>Age Restricted</th>
                <th>Has Deposit</th>
                <th>Linked Stores</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let product of chainProducts">
                <td>
                  <div class="product-name">{{ product.name }}</div>
                </td>
                <td>{{ product.barcode || '—' }}</td>
                <td>{{ product.sku || '—' }}</td>
                <td>
                  <span class="status-chip" [ngClass]="product.ageRestricted ? 'chip-warn' : 'chip-neutral'">
                    {{ product.ageRestricted ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="status-chip" [ngClass]="product.hasDeposit ? 'chip-info' : 'chip-neutral'">
                    {{ product.hasDeposit ? product.depositCategory || 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <button class="btn btn-outline-secondary btn-sm" (click)="openLinkedProductsDialog(product)">
                    <nb-icon icon="link-2-outline"></nb-icon>
                    {{ product.linkedProductsCount ?? (product.linkedProducts?.length ?? 0) }}
                  </button>
                </td>
                <td class="actions-cell">
                  <button class="btn btn-sm btn-outline-primary" (click)="openEditChainDialog(product)" title="Edit">
                    <nb-icon icon="edit-2-outline"></nb-icon>
                  </button>
                  <button class="btn btn-sm btn-outline-warning" (click)="syncProduct(product)" title="Force sync">
                    <nb-icon icon="sync-outline"></nb-icon>
                  </button>
                  <button class="btn btn-sm btn-outline-danger" (click)="deleteChainProduct(product)" title="Delete">
                    <nb-icon icon="trash-2-outline"></nb-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Chain Pagination -->
        <nb-card-footer *ngIf="chainTotalPages > 1">
          <div class="d-flex justify-content-between align-items-center">
            <div class="pagination-info">
              Showing {{ (chainPage - 1) * chainLimit + 1 }} to
              {{ Math.min(chainPage * chainLimit, chainTotal) }} of
              {{ chainTotal }} products
            </div>
            <nav aria-label="Chain catalog pagination">
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" [class.disabled]="chainPage <= 1">
                  <button class="page-link" (click)="onChainPageChange(chainPage - 1)" [disabled]="chainPage <= 1">
                    Previous
                  </button>
                </li>
                <li class="page-item" [class.active]="chainPage === 1" *ngIf="chainTotalPages > 1">
                  <button class="page-link" (click)="onChainPageChange(1)">1</button>
                </li>
                <li class="page-item disabled" *ngIf="chainPage > 4">
                  <span class="page-link">...</span>
                </li>
                <li class="page-item"
                  [class.active]="chainPage === p"
                  *ngFor="let p of getChainPaginationPages()"
                  [attr.aria-current]="chainPage === p ? 'page' : null">
                  <button class="page-link" (click)="onChainPageChange(p)">{{ p }}</button>
                </li>
                <li class="page-item disabled" *ngIf="chainPage < chainTotalPages - 3">
                  <span class="page-link">...</span>
                </li>
                <li class="page-item" [class.active]="chainPage === chainTotalPages" *ngIf="chainTotalPages > 1 && chainPage !== chainTotalPages">
                  <button class="page-link" (click)="onChainPageChange(chainTotalPages)">{{ chainTotalPages }}</button>
                </li>
                <li class="page-item" [class.disabled]="chainPage >= chainTotalPages">
                  <button class="page-link" (click)="onChainPageChange(chainPage + 1)" [disabled]="chainPage >= chainTotalPages">
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </nb-card-footer>
      </div>
    </nb-tab>
  </nb-tabset>
</div>
